import dayjs from '@/components/Dayjs';
import { UNITS } from '@/lib/constants';
import { StatusEnum, UnitName } from '@prisma/client';
import { z } from 'zod';

export const OrganizationProductKeysGetSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  pageSize: z.coerce.number().int().positive().max(100).optional().default(10),
  organizationId: z.string(),
  search: z.string().trim().max(120).optional().default(''),
  status: z.nativeEnum(StatusEnum).optional(),
  unit: z.nativeEnum(UnitName).optional(),
  assignmentState: z
    .enum(['all', 'assigned', 'unassigned'])
    .optional()
    .default('all'),
});

export type OrganizationProductKeysGetQuery = z.infer<
  typeof OrganizationProductKeysGetSchema
>;

export const ProductKeySchema = z
  .object({
    copyAmount: z
      .string()
      .min(1, { message: 'Prouct key copy amount must be at least 1' })
      .max(30, { message: 'Product key copy amount must be at most 30' }),
    startDate: z.string().date(),
    endDate: z.string().date(),
    units: z.array(z.string()),
    collectionIds: z.array(z.string().cuid()).optional(),
  })
  .refine(
    (data) => data.units.length > 0 || Boolean(data.collectionIds?.length),
    {
      message: 'Select at least one Unit or Collection.',
      path: ['units'],
    }
  )
  .refine(
    (data) => {
      const start = dayjs(data.startDate).utc().startOf('day');
      const end = dayjs(data.endDate).utc().endOf('day');
      return (
        start.isValid() &&
        end.isValid() &&
        (end.isAfter(start) || end.isSame(start)) &&
        isUtcEndDateOnOrAfterToday(data.endDate)
      );
    },
    {
      message: 'End date must be on/after start date and in the future',
      path: ['endDate'],
    }
  );

export type ProductKeyCreateFormData = z.infer<typeof ProductKeySchema>;

const validUnitValues = UNITS.map((unit) => unit.value);
const isUtcEndDateOnOrAfterToday = (rawEndDate: string) => {
  const end = dayjs(rawEndDate).utc().endOf('day');
  const today = dayjs().utc().startOf('day');
  return end.isAfter(today) || end.isSame(today);
};

export const OrganizationProductKeyCreateSchema = z
  .object({
    startDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
      message: 'Invalid date format for Start Date',
    }),
    endDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
      message: 'Invalid date format for End Date',
    }),
    copyAmount: z.coerce.number().int().min(1).max(30),
    units: z
      .array(z.string())
      .refine(
        (units) =>
          units.every((unitId) => validUnitValues.includes(unitId as UnitName)),
        { message: 'Invalid unit Value provided.' }
      ),
    collectionIds: z.array(z.string().cuid()).optional(),
  })
  .refine(
    (data) => data.units.length > 0 || Boolean(data.collectionIds?.length),
    {
      message: 'Select at least one Unit or Collection.',
      path: ['units'],
    }
  )
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: 'End date must be on or after start date.',
    path: ['endDate'],
  })
  .refine((data) => isUtcEndDateOnOrAfterToday(data.endDate), {
    message: 'End date must be today or later.',
    path: ['endDate'],
  });

export type ProductKeyFormData = z.infer<
  typeof OrganizationProductKeyCreateSchema
>;

export const OrganizationProductKeyUpdateSchema = z
  .object({
    startDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
      message: 'Invalid date format for Start Date',
    }),
    endDate: z.string().refine((date) => !isNaN(Date.parse(date)), {
      message: 'Invalid date format for End Date',
    }),
    units: z
      .array(z.string())
      .refine(
        (units) =>
          units.every((unitId) => validUnitValues.includes(unitId as UnitName)),
        { message: 'Invalid unit ID provided.' }
      ),
    collectionIds: z.array(z.string().cuid()).optional(),
    assignedToEmail: z.string().nullable().optional(),
    status: z.nativeEnum(StatusEnum).optional(),
  })
  .refine(
    (data) => data.units.length > 0 || Boolean(data.collectionIds?.length),
    {
      message: 'Select at least one Unit or Collection.',
      path: ['units'],
    }
  )
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: 'End date must be on or after start date.',
    path: ['endDate'],
  })
  .refine((data) => isUtcEndDateOnOrAfterToday(data.endDate), {
    message: 'End date must be today or later.',
    path: ['endDate'],
  });

export type ProductKeyUpdateFormData = z.infer<
  typeof OrganizationProductKeyUpdateSchema
>;
